import { useEffect, useState } from "react";

/**
 * Returns the integer number of seconds since `startedAt` was set.
 * Pass `null` to indicate "not running" — the hook returns 0 and stops
 * the interval. Used to render "Generating try-on… 12s" labels.
 *
 * Internally ticks every 100ms so callers that need a smooth progress
 * bar can read `useElapsedFraction` (below) without re-mounting; both
 * hooks share the same underlying timer cadence so they stay in sync.
 */
export function useElapsedSeconds(startedAt: number | null): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (startedAt == null) {
      setN(0);
      return;
    }
    setN(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    const id = setInterval(() => {
      setN(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return n;
}

/**
 * Returns the elapsed fractional seconds since `startedAt` was set,
 * ticking every 100ms — used by the regen button's progress bar so the
 * fill animates smoothly over its target duration instead of jumping
 * once per whole second.
 */
export function useElapsedFraction(startedAt: number | null): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (startedAt == null) {
      setV(0);
      return;
    }
    setV(Math.max(0, (Date.now() - startedAt) / 1000));
    const id = setInterval(() => {
      setV(Math.max(0, (Date.now() - startedAt) / 1000));
    }, 100);
    return () => clearInterval(id);
  }, [startedAt]);
  return v;
}
